/**
 * I-Bond purchase scheduler — Phase 8 Session 3.
 *
 * Treasury Direct caps Series I Savings Bond purchases at:
 *   - $10,000/SSN/calendar-year (electronic)
 *   - $5,000/SSN/calendar-year (paper, via tax refund only)
 *   - $10,000/SSN/calendar-year (per trust EIN, separate cap)
 *
 * Hearth's helper enforces the $10k/electronic cap per adult-SSN-year
 * and surfaces a multi-year schedule to reach a savings target. The
 * paper-via-tax-refund route is documented but deferred: it requires
 * filing logistics that don't fit a pure-math helper. Trust EINs are
 * also deferred — Phase 11 estate-planning work touches that.
 *
 * Composition rates: I-bonds combine a fixed rate (set at purchase,
 * locked for 30y) and a semi-annual variable rate (CPI-adjusted).
 * Helper takes the assumed composite rate as input — the caller
 * supplies the current Treasury Direct snapshot.
 */

export interface IBondPurchaseRung {
  readonly year: number;
  readonly purchase_cents: number;
  readonly cumulative_principal_cents: number;
  readonly purchase_window: string;
  readonly notes?: string;
}

export interface IBondScheduleResult {
  readonly rungs: readonly IBondPurchaseRung[];
  readonly years_to_target: number;
  readonly cap_cents_per_year: number;
}

/** Default annual electronic cap per SSN (TD limit). */
export const I_BOND_CAP_CENTS_PER_SSN_PER_YEAR = 1_000_000; // $10k

export function iBondPurchaseSchedule(args: {
  readonly target_total_cents: number;
  readonly num_adults: number;
  readonly start_year?: number;
}): IBondScheduleResult {
  if (args.num_adults <= 0) {
    throw new Error("iBondPurchaseSchedule: num_adults must be > 0");
  }
  const annualCap = I_BOND_CAP_CENTS_PER_SSN_PER_YEAR * args.num_adults;
  const startYear = args.start_year ?? new Date().getUTCFullYear();
  const years = Math.ceil(args.target_total_cents / annualCap);

  const rungs: IBondPurchaseRung[] = [];
  let cumulative = 0;
  for (let y = 0; y < years; y += 1) {
    const remaining = args.target_total_cents - cumulative;
    const buy = Math.min(annualCap, remaining);
    cumulative += buy;
    const isLast = y === years - 1;
    const isLessThanCap = buy < annualCap;
    rungs.push({
      year: startYear + y,
      purchase_cents: buy,
      cumulative_principal_cents: cumulative,
      purchase_window: `${startYear + y}-01-01 to ${startYear + y}-12-31`,
      ...(isLast && isLessThanCap
        ? { notes: "Final tranche — under the annual cap" }
        : {}),
    });
  }

  return {
    rungs,
    years_to_target: years,
    cap_cents_per_year: annualCap,
  };
}

/**
 * Estimate accumulated value at a given horizon assuming a constant
 * composite rate. I-bonds compound semi-annually; this approximates
 * with a yearly compound for simplicity (close enough at typical
 * rates; off by single-digit basis points over multi-year horizons).
 */
export function projectIBondValue(args: {
  readonly schedule: IBondScheduleResult;
  readonly composite_rate_bp: number;
  readonly horizon_year: number;
}): number {
  const r = args.composite_rate_bp / 10000;
  let total = 0;
  for (const rung of args.schedule.rungs) {
    const yearsHeld = Math.max(0, args.horizon_year - rung.year);
    total += rung.purchase_cents * Math.pow(1 + r, yearsHeld);
  }
  return Math.round(total);
}
